import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import './Settings.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface DietaryOption {
  id: string;
  label: string;
  value: string;
}

interface CarouselItem {
  id: string;
  title: string;
  body: string;
  design: 'primary' | 'secondary' | 'success' | 'info' | 'light';
  order: number;
  enabled: boolean;
}

interface SystemSettings {
  id: string;
  type: string;
  organizerDefaults: {
    pricePerEvent: number;
    netDays: number;
  };
  eventDefaults: {
    appetizerDuration: number;
    mainCourseDuration: number;
    dessertDuration: number;
    defaultParticipants: number;
    defaultRadiusKm: number;
    firstStopEmailMinutesBefore: number;
  };
  dietaryOptions: DietaryOption[];
  carouselItems: CarouselItem[];
  carouselAutoPlayInterval?: number;
  statisticsDefaults?: {
    loginLogsTtlDays?: number;
  };
  automationDefaults?: {
    timezone: string;
    dailyCheckTime: string;
    lowParticipantWarningDays: number;
    cancellationDeadlineDays: number;
    registrationCloseDays: number;
    registrationCloseTime: string;
    routePlanningDays: number;
    routePlanningTime: string;
    hostEmailTime: string;
    participantReminderDays: number;
    participantReminderTime: string;
    feedbackEmailDaysAfter: number;
    feedbackEmailTime: string;
  };
}

const Settings: React.FC = () => {
  const { accessToken } = useAuth();
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState<'defaults' | 'dietary' | 'carousel' | 'automation'>('defaults');
  
  // Form state
  const [pricePerEvent, setPricePerEvent] = useState(1000);
  const [netDays, setNetDays] = useState(14);
  const [appetizerDuration, setAppetizerDuration] = useState(90);
  const [mainCourseDuration, setMainCourseDuration] = useState(120);
  const [dessertDuration, setDessertDuration] = useState(90);
  const [defaultParticipants, setDefaultParticipants] = useState(20);
  const [defaultRadiusKm, setDefaultRadiusKm] = useState(10);
  const [carouselAutoPlayInterval, setCarouselAutoPlayInterval] = useState(6000);
  const [loginLogsTtlDays, setLoginLogsTtlDays] = useState(180);
  const [dietaryOptions, setDietaryOptions] = useState<DietaryOption[]>([]);
  
  // Automation defaults state
  const [timezone, setTimezone] = useState('Europe/Oslo');
  const [dailyCheckTime, setDailyCheckTime] = useState('00:15');
  const [lowParticipantWarningDays, setLowParticipantWarningDays] = useState(8);
  const [cancellationDeadlineDays, setCancellationDeadlineDays] = useState(6);
  const [registrationCloseDays, setRegistrationCloseDays] = useState(5);
  const [registrationCloseTime, setRegistrationCloseTime] = useState('23:59');
  const [routePlanningDays, setRoutePlanningDays] = useState(4);
  const [routePlanningTime, setRoutePlanningTime] = useState('05:00');
  const [hostEmailTime, setHostEmailTime] = useState('10:00');
  const [participantReminderDays, setParticipantReminderDays] = useState(2);
  const [participantReminderTime, setParticipantReminderTime] = useState('18:00');
  const [firstStopEmailMinutesBefore, setFirstStopEmailMinutesBefore] = useState(30);
  const [feedbackEmailDaysAfter, setFeedbackEmailDaysAfter] = useState(1);
  const [feedbackEmailTime, setFeedbackEmailTime] = useState('10:00');
  
  // New dietary option form
  const [newDietaryId, setNewDietaryId] = useState('');
  const [newDietaryLabel, setNewDietaryLabel] = useState('');

  // Carousel items state
  const [carouselItems, setCarouselItems] = useState<CarouselItem[]>([]);
  const [newCarouselTitle, setNewCarouselTitle] = useState('');
  const [newCarouselBody, setNewCarouselBody] = useState('');
  const [newCarouselDesign, setNewCarouselDesign] = useState<CarouselItem['design']>('primary');

  useEffect(() => {
    fetchSettings();
  }, [accessToken]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/admin/settings/system`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      
      const data = response.data;
      setSettings(data);
      
      // Populate form fields
      setPricePerEvent(data.organizerDefaults?.pricePerEvent || 1000);
      setNetDays(data.organizerDefaults?.netDays || 14);
      setAppetizerDuration(data.eventDefaults?.appetizerDuration || 90);
      setMainCourseDuration(data.eventDefaults?.mainCourseDuration || 120);
      setDessertDuration(data.eventDefaults?.dessertDuration || 90);
      setDefaultParticipants(data.eventDefaults?.defaultParticipants || 20);
      setDefaultRadiusKm(data.eventDefaults?.defaultRadiusKm || 10);
      setCarouselAutoPlayInterval(data.carouselAutoPlayInterval || 6000);
      setFirstStopEmailMinutesBefore(data.eventDefaults?.firstStopEmailMinutesBefore || 30);
      setLoginLogsTtlDays(data.statisticsDefaults?.loginLogsTtlDays || 180);
      setDietaryOptions(data.dietaryOptions || []);
      setCarouselItems(data.carouselItems || []);
      
      // Populate automation defaults
      if (data.automationDefaults) {
        setTimezone(data.automationDefaults.timezone || 'Europe/Oslo');
        setDailyCheckTime(data.automationDefaults.dailyCheckTime || '00:15');
        setLowParticipantWarningDays(data.automationDefaults.lowParticipantWarningDays || 8);
        setCancellationDeadlineDays(data.automationDefaults.cancellationDeadlineDays || 6);
        setRegistrationCloseDays(data.automationDefaults.registrationCloseDays || 5);
        setRegistrationCloseTime(data.automationDefaults.registrationCloseTime || '23:59');
        setRoutePlanningDays(data.automationDefaults.routePlanningDays || 4);
        setRoutePlanningTime(data.automationDefaults.routePlanningTime || '05:00');
        setHostEmailTime(data.automationDefaults.hostEmailTime || '10:00');
        setParticipantReminderDays(data.automationDefaults.participantReminderDays || 2);
        setParticipantReminderTime(data.automationDefaults.participantReminderTime || '18:00');
        setFeedbackEmailDaysAfter(data.automationDefaults.feedbackEmailDaysAfter || 1);
        setFeedbackEmailTime(data.automationDefaults.feedbackEmailTime || '10:00');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kunne ikke hente innstillinger');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const updates = {
        organizerDefaults: {
          pricePerEvent: Number(pricePerEvent),
          netDays: Number(netDays),
        },
        eventDefaults: {
          appetizerDuration: Number(appetizerDuration),
          mainCourseDuration: Number(mainCourseDuration),
          dessertDuration: Number(dessertDuration),
          defaultParticipants: Number(defaultParticipants),
          defaultRadiusKm: Number(defaultRadiusKm),
          firstStopEmailMinutesBefore: Number(firstStopEmailMinutesBefore),
        },
        dietaryOptions,
        carouselItems,
        carouselAutoPlayInterval: Number(carouselAutoPlayInterval),
        statisticsDefaults: {
          loginLogsTtlDays: Number(loginLogsTtlDays),
        },
        automationDefaults: {
          timezone,
          dailyCheckTime,
          lowParticipantWarningDays: Number(lowParticipantWarningDays),
          cancellationDeadlineDays: Number(cancellationDeadlineDays),
          registrationCloseDays: Number(registrationCloseDays),
          registrationCloseTime,
          routePlanningDays: Number(routePlanningDays),
          routePlanningTime,
          hostEmailTime,
          participantReminderDays: Number(participantReminderDays),
          participantReminderTime,
          feedbackEmailDaysAfter: Number(feedbackEmailDaysAfter),
          feedbackEmailTime,
        },
      };

      await axios.patch(`${API_URL}/admin/settings/system`, updates, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      setSuccess('Innstillinger lagret!');
      await fetchSettings();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kunne ikke lagre innstillinger');
    } finally {
      setSaving(false);
    }
  };

  const handleAddDietaryOption = () => {
    if (!newDietaryId.trim() || !newDietaryLabel.trim()) {
      alert('Både ID og Label må fylles ut');
      return;
    }

    if (dietaryOptions.some(opt => opt.id === newDietaryId)) {
      alert('En diett-opsjon med denne ID-en eksisterer allerede');
      return;
    }

    setDietaryOptions([
      ...dietaryOptions,
      {
        id: newDietaryId,
        label: newDietaryLabel,
        value: newDietaryId,
      },
    ]);

    setNewDietaryId('');
    setNewDietaryLabel('');
  };

  const handleRemoveDietaryOption = (id: string) => {
    setDietaryOptions(dietaryOptions.filter(opt => opt.id !== id));
  };

  const handleUpdateDietaryLabel = (id: string, newLabel: string) => {
    setDietaryOptions(
      dietaryOptions.map(opt =>
        opt.id === id ? { ...opt, label: newLabel } : opt
      )
    );
  };

  // Carousel management functions
  const handleAddCarouselItem = () => {
    if (!newCarouselTitle.trim() || !newCarouselBody.trim()) {
      alert('Både tittel og tekst må fylles ut');
      return;
    }

    const newItem: CarouselItem = {
      id: `carousel_${Date.now()}`,
      title: newCarouselTitle,
      body: newCarouselBody,
      design: newCarouselDesign,
      order: carouselItems.length + 1,
      enabled: true,
    };

    setCarouselItems([...carouselItems, newItem]);
    setNewCarouselTitle('');
    setNewCarouselBody('');
    setNewCarouselDesign('primary');
  };

  const handleRemoveCarouselItem = (id: string) => {
    const updatedItems = carouselItems.filter(item => item.id !== id);
    // Reorder remaining items
    const reorderedItems = updatedItems.map((item, index) => ({
      ...item,
      order: index + 1,
    }));
    setCarouselItems(reorderedItems);
  };

  const handleUpdateCarouselItem = (id: string, updates: Partial<CarouselItem>) => {
    setCarouselItems(
      carouselItems.map(item =>
        item.id === id ? { ...item, ...updates } : item
      )
    );
  };

  const handleMoveCarouselItem = (id: string, direction: 'up' | 'down') => {
    const index = carouselItems.findIndex(item => item.id === id);
    if (index === -1) return;

    const newItems = [...carouselItems];
    if (direction === 'up' && index > 0) {
      [newItems[index], newItems[index - 1]] = [newItems[index - 1], newItems[index]];
    } else if (direction === 'down' && index < newItems.length - 1) {
      [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
    }

    // Update order numbers
    const reorderedItems = newItems.map((item, idx) => ({
      ...item,
      order: idx + 1,
    }));

    setCarouselItems(reorderedItems);
  };

  if (loading) {
    return <div className="loading-state">Laster innstillinger...</div>;
  }

  return (
    <div className="settings-management">
      <div className="management-header">
        <h2>Plattforminnstillinger</h2>
        <p className="settings-subtitle">Systeminnstillinger og standardverdier</p>
      </div>

      {error && <div className="message-banner error">{error}</div>}
      {success && <div className="message-banner success">{success}</div>}

      {/* Tabs */}
      <div className="settings-tabs">
        <button
          className={`tab-button ${activeTab === 'defaults' ? 'active' : ''}`}
          onClick={() => setActiveTab('defaults')}
        >
          Standardverdier
        </button>
        <button
          className={`tab-button ${activeTab === 'automation' ? 'active' : ''}`}
          onClick={() => setActiveTab('automation')}
        >
          Automasjon
        </button>
        <button
          className={`tab-button ${activeTab === 'dietary' ? 'active' : ''}`}
          onClick={() => setActiveTab('dietary')}
        >
          Diettbehov
        </button>
        <button
          className={`tab-button ${activeTab === 'carousel' ? 'active' : ''}`}
          onClick={() => setActiveTab('carousel')}
        >
          Karusell
        </button>
      </div>

      <div className="settings-sections">
        {/* Defaults Tab */}
        {activeTab === 'defaults' && (
          <>
            {/* Organizer Defaults */}
            <div className="settings-section">
              <h3>Arrangør Standardverdier</h3>
              <div className="settings-grid">
                <div className="setting-field">
                  <label>Standard pris per arrangement (kr)</label>
                  <input
                    type="number"
                    value={pricePerEvent}
                    onChange={(e) => setPricePerEvent(Number(e.target.value))}
                    min="0"
                  />
                </div>
                <div className="setting-field">
                  <label>Standard betalingsfrist (dager)</label>
                  <input
                    type="number"
                    value={netDays}
                    onChange={(e) => setNetDays(Number(e.target.value))}
                    min="1"
                  />
                </div>
              </div>
            </div>

            {/* Event Defaults */}
            <div className="settings-section">
              <h3>Arrangement Standardverdier</h3>
              <div className="settings-grid">
                <div className="setting-field">
                  <label>Forrett varighet (minutter)</label>
                  <input
                    type="number"
                    value={appetizerDuration}
                    onChange={(e) => setAppetizerDuration(Number(e.target.value))}
                    min="1"
                  />
                </div>
                <div className="setting-field">
                  <label>Hovedrett varighet (minutter)</label>
                  <input
                    type="number"
                    value={mainCourseDuration}
                    onChange={(e) => setMainCourseDuration(Number(e.target.value))}
                    min="1"
                  />
                </div>
                <div className="setting-field">
                  <label>Dessert varighet (minutter)</label>
                  <input
                    type="number"
                    value={dessertDuration}
                    onChange={(e) => setDessertDuration(Number(e.target.value))}
                    min="1"
                  />
                </div>
                <div className="setting-field">
                  <label>Standard antall deltakere</label>
                  <input
                    type="number"
                    value={defaultParticipants}
                    onChange={(e) => setDefaultParticipants(Number(e.target.value))}
                    min="4"
                    step="2"
                  />
                </div>
                <div className="setting-field">
                  <label>Standard radius (km)</label>
                  <input
                    type="number"
                    value={defaultRadiusKm}
                    onChange={(e) => setDefaultRadiusKm(Number(e.target.value))}
                    min="1"
                  />
                </div>
                <div className="setting-field">
                  <label>Første stopp epost - minutter før start</label>
                  <input
                    type="number"
                    value={firstStopEmailMinutesBefore}
                    onChange={(e) => setFirstStopEmailMinutesBefore(Number(e.target.value))}
                    min="1"
                  />
                  <small>Deltakere får første ruteinformasjon X minutter før arrangementet</small>
                </div>
              </div>
            </div>

            {/* Landing Page Settings */}
            <div className="settings-section">
              <h3>Forside Innstillinger</h3>
              <div className="settings-grid">
                <div className="setting-field">
                  <label>Karusell auto-play intervall (millisekunder)</label>
                  <input
                    type="number"
                    value={carouselAutoPlayInterval}
                    onChange={(e) => setCarouselAutoPlayInterval(Number(e.target.value))}
                    min="1000"
                    step="1000"
                  />
                  <small>Standard: 6000 (6 sekunder)</small>
                </div>
              </div>
            </div>

            {/* Statistics Defaults */}
            <div className="settings-section">
              <h3>Statistikk Standardverdier</h3>
              <div className="settings-grid">
                <div className="setting-field">
                  <label>Innloggingslogg varighet (dager)</label>
                  <input
                    type="number"
                    value={loginLogsTtlDays}
                    onChange={(e) => setLoginLogsTtlDays(Number(e.target.value))}
                    min="1"
                    max="365"
                  />
                  <small>Hvor lenge detaljerte innloggingslogger skal lagres (standard: 180 dager)</small>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Automation Tab */}
        {activeTab === 'automation' && (
          <>
            {/* System & Schedule Settings */}
            <div className="settings-section">
              <h3>System og Tidsstyring</h3>
              <div className="settings-grid">
                <div className="setting-field">
                  <label>Tidssone</label>
                  <select
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                  >
                    <option value="Europe/Oslo">Europe/Oslo (Norge)</option>
                    <option value="Europe/Stockholm">Europe/Stockholm</option>
                    <option value="Europe/Copenhagen">Europe/Copenhagen</option>
                  </select>
                </div>
                <div className="setting-field">
                  <label>Daglig sjekk av arrangement (tidspunkt)</label>
                  <input
                    type="time"
                    value={dailyCheckTime}
                    onChange={(e) => setDailyCheckTime(e.target.value)}
                  />
                  <small>Når systemet sjekker arrangement daglig</small>
                </div>
              </div>
            </div>

            {/* Registration & Deadline Settings */}
            <div className="settings-section">
              <h3>Påmelding og Frister</h3>
              <div className="settings-grid">
                <div className="setting-field">
                  <label>Epost til arrangør om deltakerantall (dager før)</label>
                  <input
                    type="number"
                    value={lowParticipantWarningDays}
                    onChange={(e) => setLowParticipantWarningDays(Number(e.target.value))}
                    min="1"
                  />
                  <small>Varsel hvis for få deltakere eller ikke ideelt antall</small>
                </div>
                <div className="setting-field">
                  <label>Avmelding ikke lenger mulig (dager før)</label>
                  <input
                    type="number"
                    value={cancellationDeadlineDays}
                    onChange={(e) => setCancellationDeadlineDays(Number(e.target.value))}
                    min="1"
                  />
                </div>
                <div className="setting-field">
                  <label>Påmelding stenger (dager før)</label>
                  <input
                    type="number"
                    value={registrationCloseDays}
                    onChange={(e) => setRegistrationCloseDays(Number(e.target.value))}
                    min="1"
                  />
                </div>
                <div className="setting-field">
                  <label>Påmelding stenger (tidspunkt)</label>
                  <input
                    type="time"
                    value={registrationCloseTime}
                    onChange={(e) => setRegistrationCloseTime(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Route Planning Settings */}
            <div className="settings-section">
              <h3>Ruteplanlegging</h3>
              <div className="settings-grid">
                <div className="setting-field">
                  <label>Ruten planlegges (dager før)</label>
                  <input
                    type="number"
                    value={routePlanningDays}
                    onChange={(e) => setRoutePlanningDays(Number(e.target.value))}
                    min="1"
                  />
                </div>
                <div className="setting-field">
                  <label>Ruten planlegges (tidspunkt)</label>
                  <input
                    type="time"
                    value={routePlanningTime}
                    onChange={(e) => setRoutePlanningTime(e.target.value)}
                  />
                  <small>Når automatisk ruteberegning kjøres</small>
                </div>
                <div className="setting-field">
                  <label>Epost til verter om tildelt rett (tidspunkt)</label>
                  <input
                    type="time"
                    value={hostEmailTime}
                    onChange={(e) => setHostEmailTime(e.target.value)}
                  />
                  <small>Samme dag som ruten planlegges</small>
                </div>
              </div>
            </div>

            {/* Reminder Settings */}
            <div className="settings-section">
              <h3>Påminnelser</h3>
              <p className="section-description">
                Alle deltakere er også verter, så det er kun én påminnelse som sendes til alle.
              </p>
              <div className="settings-grid">
                <div className="setting-field">
                  <label>Påminnelse til deltakere (dager før)</label>
                  <input
                    type="number"
                    value={participantReminderDays}
                    onChange={(e) => setParticipantReminderDays(Number(e.target.value))}
                    min="1"
                  />
                </div>
                <div className="setting-field">
                  <label>Påminnelse til deltakere (tidspunkt)</label>
                  <input
                    type="time"
                    value={participantReminderTime}
                    onChange={(e) => setParticipantReminderTime(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Post-Event Settings */}
            <div className="settings-section">
              <h3>Oppfølging etter Arrangement</h3>
              <div className="settings-grid">
                <div className="setting-field">
                  <label>Oppfølging med takk og evaluering (dager etter)</label>
                  <input
                    type="number"
                    value={feedbackEmailDaysAfter}
                    onChange={(e) => setFeedbackEmailDaysAfter(Number(e.target.value))}
                    min="1"
                  />
                </div>
                <div className="setting-field">
                  <label>Oppfølging (tidspunkt)</label>
                  <input
                    type="time"
                    value={feedbackEmailTime}
                    onChange={(e) => setFeedbackEmailTime(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Dietary Options Tab */}
        {activeTab === 'dietary' && (
          <div className="settings-section">
            <h3>Diettbehov Alternativer</h3>
            
            <div className="dietary-options-list">
              {dietaryOptions.map((option) => (
                <div key={option.id} className="dietary-option-item">
                  <input
                    type="text"
                    value={option.id}
                    disabled
                    className="dietary-id"
                  />
                  <input
                    type="text"
                    value={option.label}
                    onChange={(e) => handleUpdateDietaryLabel(option.id, e.target.value)}
                    className="dietary-label"
                  />
                  <button
                    onClick={() => handleRemoveDietaryOption(option.id)}
                    className="btn-remove"
                  >
                    Fjern
                  </button>
                </div>
              ))}
            </div>

            <div className="add-dietary-option">
              <input
                type="text"
                placeholder="ID (f.eks. 'vegetarian')"
                value={newDietaryId}
                onChange={(e) => setNewDietaryId(e.target.value)}
                className="dietary-id"
              />
              <input
                type="text"
                placeholder="Label (f.eks. 'Vegetar')"
                value={newDietaryLabel}
                onChange={(e) => setNewDietaryLabel(e.target.value)}
                className="dietary-label"
              />
              <button onClick={handleAddDietaryOption} className="btn-add">
                Legg til
              </button>
            </div>
          </div>
        )}

        {/* Carousel Tab */}
        {activeTab === 'carousel' && (
          <div className="settings-section">
            <h3>Karusell Innhold</h3>
            
            <div className="carousel-items-list">
              {carouselItems
                .sort((a, b) => a.order - b.order)
                .map((item, index) => (
                  <div key={item.id} className="carousel-item-editor">
                    <div className="carousel-item-header">
                      <span className="carousel-order">#{item.order}</span>
                      <div className="carousel-controls">
                        <button
                          onClick={() => handleMoveCarouselItem(item.id, 'up')}
                          disabled={index === 0}
                          className="btn-move"
                          title="Flytt opp"
                        >
                          ↑
                        </button>
                        <button
                          onClick={() => handleMoveCarouselItem(item.id, 'down')}
                          disabled={index === carouselItems.length - 1}
                          className="btn-move"
                          title="Flytt ned"
                        >
                          ↓
                        </button>
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={item.enabled}
                            onChange={(e) =>
                              handleUpdateCarouselItem(item.id, { enabled: e.target.checked })
                            }
                          />
                          Aktiv
                        </label>
                        <button
                          onClick={() => handleRemoveCarouselItem(item.id)}
                          className="btn-remove"
                        >
                          Fjern
                        </button>
                      </div>
                    </div>
                    
                    <div className="carousel-item-fields">
                      <div className="field-group">
                        <label>Tittel</label>
                        <input
                          type="text"
                          value={item.title}
                          onChange={(e) =>
                            handleUpdateCarouselItem(item.id, { title: e.target.value })
                          }
                          className="carousel-title-input"
                        />
                      </div>
                      
                      <div className="field-group">
                        <label>Tekst</label>
                        <textarea
                          value={item.body}
                          onChange={(e) =>
                            handleUpdateCarouselItem(item.id, { body: e.target.value })
                          }
                          className="carousel-body-input"
                          rows={3}
                        />
                      </div>
                      
                      <div className="field-group">
                        <label>Design</label>
                        <select
                          value={item.design}
                          onChange={(e) =>
                            handleUpdateCarouselItem(item.id, {
                              design: e.target.value as CarouselItem['design'],
                            })
                          }
                          className="carousel-design-select"
                        >
                          <option value="primary">Primær (Blå)</option>
                          <option value="secondary">Sekundær (Grønn)</option>
                          <option value="success">Suksess (Lysgrønn)</option>
                          <option value="info">Info (Lyseblå)</option>
                          <option value="light">Lys (Grå)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            <div className="add-carousel-item">
              <h4>Legg til ny karusell</h4>
              <div className="field-group">
                <label>Tittel</label>
                <input
                  type="text"
                  placeholder="F.eks. 'En uforglemmelig kveld'"
                  value={newCarouselTitle}
                  onChange={(e) => setNewCarouselTitle(e.target.value)}
                  className="carousel-title-input"
                />
              </div>
              
              <div className="field-group">
                <label>Tekst</label>
                <textarea
                  placeholder="Skriv din tekst her..."
                  value={newCarouselBody}
                  onChange={(e) => setNewCarouselBody(e.target.value)}
                  className="carousel-body-input"
                  rows={3}
                />
              </div>
              
              <div className="field-group">
                <label>Design</label>
                <select
                  value={newCarouselDesign}
                  onChange={(e) => setNewCarouselDesign(e.target.value as CarouselItem['design'])}
                  className="carousel-design-select"
                >
                  <option value="primary">Primær (Blå)</option>
                  <option value="secondary">Sekundær (Grønn)</option>
                  <option value="success">Suksess (Lysgrønn)</option>
                  <option value="info">Info (Lyseblå)</option>
                  <option value="light">Lys (Grå)</option>
                </select>
              </div>
              
              <button onClick={handleAddCarouselItem} className="btn-add">
                Legg til karusell
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="settings-actions">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn btn-primary"
        >
          {saving ? 'Lagrer...' : 'Lagre endringer'}
        </button>
      </div>
    </div>
  );
};

export default Settings;
